import {Theme, alpha} from '@mui/material'
import Card from '@mui/material/Card'
import {
  amber,
  blue,
  blueGrey,
  green,
  grey,
  indigo,
  lightBlue,
  orange,
  purple,
  red,
  teal,
} from '@mui/material/colors'
import {createTheme, styled} from '@mui/material/styles'
import {makeStyles} from '@mui/styles'

export const useBlur = makeStyles(() => ({
  blur: {
    boxShadow: 'rgba(99, 99, 99, 0.2) 0px 2px 8px 0px',
    margin: '15px',
    '& > :not(.nomask)': {
      filter: 'blur(4px)',
      // boxShadow: '0 0 0 10px hsl(0, 0%, 80%),0 0 0 15px hsl(0, 0%, 90%)',
    },
  },
  nomask: {
    textAlign: 'right',
    float: 'right',
    position: 'absolute',
    top: 0,
    right: 0,
  },
}))

export const useTextAreaStyle = makeStyles((theme) => ({
  textArea: {
    width: '100%',
    fontFamily: 'IBM Plex Sans, sans-serif',
    fontSize: '0.875rem',
    fontWeight: 400,
    lineHeight: 1.5,
    padding: '12px',
    borderRadius: '12px 12px 0 12px',
    color: 'grey',
    background: '#ffffff',
    border: `1px solid ${grey[200]}`,
    boxShadow: `0px 2px 2px ${grey[50]}`,
    '&:hover': {
      borderColor: `${blue[400]}`,
    },

    '&:focus': {
      borderColor: `${blue[400]}`,
      boxShadow: `0 0 0 3px ${blue[200]}`,
    },

    // firefox
    '&:focus-visible': {
      outline: 0,
    },
  },
}))

export const useDisableRouterLink = makeStyles((theme) => ({
  disableLink: {
    pointerEvents: 'none',
  },
}))

export const useMapMarkers = makeStyles((theme) => ({
  marker: {
    width: 0,
    height: 0,
    '& span': {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      boxSizing: 'border-box',
      width: '30px',
      height: '30px',
      color: '#fff',
      background: '#4D2D73',
      border: 'solid 2px',
      borderRadius: '0 70% 70%',
      boxShadow: '0 0 2px #000',
      cursor: 'pointer',
      transformOrigin: '0 0',
      transform: 'rotateZ(-135deg)',
    },
    '& b': {
      transform: 'rotateZ(135deg)',
    },
  },
}))

export const useDividerStyle = makeStyles((theme) => ({
  container: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
  },
  borderLeft: {
    borderBottom: '1px solid grey',
    width: '10%',
  },
  borderRight: {
    borderBottom: '1px solid grey',
    width: '100%',
  },
  content: {
    paddingTop: '1em',
    paddingBottom: '1em',
    paddingRight: '.5em',
    paddingLeft: '.5em',
    fontWeight: 500,
    fontSize: 15,
    color: 'white',
  },
}))

export const useProgressWrapper = makeStyles((theme) => ({
  progressWrapper: {
    width: 'fit-content',
    marginLeft: 'auto',
    marginRight: 'auto',
    display: 'flex',
    alignItems: 'center',
    backgroundColor: 'white',
    height: '100%',
    flex: '1 0 auto',
  },
  modalPaper: {
    minHeight: 200,
  },
}))

export const useProfileStyle = makeStyles(() => ({
  cardWrapper: {
    '&:not(:last-of-type)': {
      marginBottom: 2,
    },
  },
  placeholderImageWrapper: {
    width: 'fit-content',
  },
  placeholderContent: {
    textAlign: 'center',
    paddingTop: 4,
    paddingBottom: 4,
  },
}))

export const useMain = makeStyles(() => ({
  container: {
    display: 'flex',
    flexWrap: 'wrap',
    marginLeft: 'auto',
    marginRight: 'auto',
    width: '100%',
    '--bs-gutter-x': '1.5rem',
    '--bs-gutter-y': 0,
    justifyContent: 'center',
    '@media (min-width: 576px)': {
      maxWidth: '540px',
      padding: '0 5px',
    },
    '@media (min-width: 768px)': {
      maxWidth: '720px',
      padding: '0 5px',
    },
    '@media (min-width: 992px)': {
      padding: '0 30px',
      maxWidth: '960px',
    },
    '@media (min-width: 1200px)': {
      maxWidth: '1140px',
    },
    '@media (min-width: 1400px)': {
      maxWidth: '1320px',
    },
  },
}))

export const useIframeWrapper = makeStyles(() => ({
  container: {
    position: 'relative',
    overflow: 'hidden',
    width: '100%',
  },
  resonsiveIframe: {
    position: 'relative',
    top: 0,
    left: 0,
    bottom: 0,
    right: 0,
    width: '100rem',
    height: '1000px',
    border: 'none',
    overflow: 'hidden',
  },
}))

export const useStylesTransferList = makeStyles(() => ({
  expandIcon: {
    transform: 'rotate(90deg)',
  },
  card: {
    borderRadius: '0 !important',
    boxShadow: 'none',
  },
}))

interface StyleProps {
  stepColor: string
  bgColor: string
}

export const useStylesStepTransfer = makeStyles<Theme, StyleProps>((theme) => ({
  crumbsUL: {
    listStyle: 'none',
    display: 'inline-table',
  },
  crumbsULLI: {
    display: 'block',
    cursor: 'pointer',
    float: 'left',
    height: '20px',
    backgroundColor: ({bgColor}: {bgColor: string}) => bgColor,
    textAlign: 'center',
    padding: '3px 4px 4px 15px',
    position: 'relative',
    margin: '0 4px 0 0',
    fontSize: '.9em',
    textDecoration: 'none',
    color: '#fff',
    /* makes first on square, radius */
    '&:first-child': {
      borderTopLeftRadius: '0px',
      borderBottomLeftRadius: '0px',
      '& ::before': {
        display: 'none',
      },
    },
    /* makes last one square */
    // '&:last-child': {
    //   paddingRight: '10px',
    //   borderTopRightRadius: '2px',
    //   borderBottomRightRadius: '2px',
    //   '& ::after': {
    //     display: 'none',
    //   },
    // },
    '& ::before': {
      content: '""',
      borderTop: '10px solid transparent',
      borderBottom: '10px solid transparent',
      borderLeft: '10px solid white',
      position: 'absolute',
      left: 0,
      top: 0,
    },
    '& ::after': {
      content: '""',
      borderTop: '10px solid transparent',
      borderBottom: '10px solid transparent',
      borderLeft: ({stepColor}: {stepColor: string}) => stepColor,
      position: 'absolute',
      right: '-10px',
      top: 0,
      zIndex: 1,
    },
    '&:hover': {
      borderBottom: '2px solid #0f6',
      '& :after': {
        borderLeftColor: '2px solid #fa5ba5',
      },
    },
  },
}))

export const useStylesTags = makeStyles(() => ({
  tagStyle: {
    display: 'inline-block',
    height: '24px',
    lineHeight: '24px',
    position: 'relative',
    padding: '0 10px 0 12px',
    background: '#008a1e',
    '-webkit-border-bottom-right-radius': '3px',
    borderBottomRightRadius: '3px',
    '-webkit-border-top-right-radius': '3px',
    borderTopRightRadius: '3px',
    '-webkit-box-shadow': '0 1px 2px rgba(0,0,0,0.2)',

    '-webkit-border-bottom-left-radius': '1px',
    borderBottomLeftRadius: '1px',
    '-webkit-border-top-left-radius': '1px',
    borderTopLeftRadius: '1px',

    boxShadow: '0 1px 2px rgba(0,0,0,0.2)',
    color: '#fff',
    fontSize: '12px',
    fontFamily: '"Lucida Grande","Lucida Sans Unicode",Verdana,sans-serif',
    textDecoration: 'none',
    textShadow: '0 1px 2px rgba(0,0,0,0.2)',
    fontWeight: 'bold',
    '& span:before': {
      content: "''",
      position: 'absolute',
      top: 0,
      left: '-12px',
      width: 0,
      height: 0,
      borderColor: 'transparent #008a1e transparent transparent',
      borderStyle: 'solid',
      borderWidth: '12px 12px 12px 0',
    },
    '& span:after': {
      content: "''",
      position: 'absolute',
      top: '10px',
      left: '-6px',
      float: 'left',
      width: '5px',
      height: '5px',
      '-webkit-border-radius': '50%',
      borderRadius: '50%',
      background: '#0f6',
      '-webkit-box-shadow': '-1px -1px 2px rgba(0,0,0,0.4)',
      boxShadow: '-1px -1px 2px rgba(0,0,0,0.4)',
    },
  },
}))

export const useStylesCommon = makeStyles(() => ({
  boxWrapper: {
    display: 'grid',
    gridTemplateColumns: '1fr',
  },
  card: {
    borderRadius: '0 !important',
    padding: '1rem',
  },
  helperText: {
    color: red[500],
    fontSize: '1.1em',
  },
  validationTextError: {
    color: red[500],
    fontSize: '1rem',
    weight: 600,
  },
  styledSelect: {
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: grey[800],
    paddingLeft: '15px',
    paddingRight: '15px',
    boxShadow: 'rgba(255, 255, 255, 0.24) 1px 1px 1px 1px',
    fontFamily: `'Roboto','Helvetica','Arial','sans-serif'`,
    position: 'relative',
    width: 'auto',
    '-webkit-appearance': 'none',
    '& after': {
      outline: 'none !important',
      pointerEvents: 'none',
    },
    '& focus-visible': {
      outline: 'none !important',
    },
  },
  styledSelectText: {
    position: 'relative',
    fontFamily: 'inherit',
    color: grey[900],
    backgroundColor: grey[300],
    width: 'auto',
    padding: '4px 8px 4px 8px',
    fontSize: '12px',
    borderRadius: '10px',
    border: 'none',
    appearance: 'none',
    cursor: 'pointer',
    '-webkit-appearance': 'none',
    borderBottom: '1px solid rgba(0,0,0, 0.12)',
    '& focus': {
      outline: 'none',
    },
    '& focus-visible': {
      outline: 'none !important',
    },
  },
  styledSelectLabel: {
    color: blue[300],
    textShadow: '1px 1px 1px black',
    fontWeight: 600,
    top: '-20px',
    transition: '0.2s ease all',
    fontSize: '12px',
    '& focus-visible': {
      outline: 'none !important',
    },
  },
  styledSelectBar: {
    position: 'relative',
    display: 'block',
    width: 'auto',
    '& after': {
      content: '',
      height: '2px',
      bottom: '1px',
      position: 'absolute',
      background: '#2F80ED',
      transition: '0.2s ease all',
      right: '50%',
      width: '50%',
    },
    '& before': {
      content: '',
      height: '2px',
      width: 0,
      bottom: '1px',
      position: 'absolute',
      background: '#2F80ED',
      transition: '0.2s ease all',
      left: '50%',
    },
    '& focus-visible': {
      outline: 'none !important',
    },
  },
  styledSelectHighlight: {
    position: 'absolute',
    height: '60%',
    width: '100px',
    top: '25%',
    left: 0,
    pointerEvents: 'none',
    opacity: 0.5,
    '& focus-visible': {
      outline: 'none !important',
    },
  },
  // for ecosystem select
  styledSelectEcosystem: {
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: grey[50],
    paddingLeft: '25px',
    paddingRight: '25px',
    fontFamily: `'Roboto','Helvetica','Arial','sans-serif'`,
    position: 'relative',
    width: 'auto',
    '-webkit-appearance': 'none',
    '& after': {
      outline: 'none !important',
      pointerEvents: 'none',
    },
    '& focus-visible': {
      outline: 'none !important',
    },
  },
  styledSelectEcosystemText: {
    position: 'relative',
    fontFamily: 'inherit',
    color: grey[900],
    backgroundColor: grey[300],
    width: 'auto',
    padding: '4px 8px 4px 8px',
    fontSize: '12px',
    borderRadius: '10px',
    border: 'none',
    appearance: 'none',
    cursor: 'pointer',
    '-webkit-appearance': 'none',
    borderBottom: '1px solid rgba(0,0,0, 0.12)',
    '& focus': {
      outline: 'none',
    },
    '& focus-visible': {
      outline: 'none !important',
    },
  },
  styledSelectEcosystemLabel: {
    color: grey[800],
    fontWeight: 600,
    top: '-20px',
    transition: '0.2s ease all',
    fontSize: '12px',
    '& focus-visible': {
      outline: 'none !important',
    },
  },
  styledSelectEcosystemBar: {
    position: 'relative',
    display: 'block',
    width: 'auto',
    '& after': {
      content: '',
      height: '2px',
      bottom: '1px',
      position: 'absolute',
      background: '#2F80ED',
      transition: '0.2s ease all',
      right: '50%',
      width: '50%',
    },
    '& before': {
      content: '',
      height: '2px',
      width: 0,
      bottom: '1px',
      position: 'absolute',
      background: '#2F80ED',
      transition: '0.2s ease all',
      left: '50%',
    },
    '& focus-visible': {
      outline: 'none !important',
    },
  },
  styledSelectEcosystemHighlight: {
    position: 'absolute',
    height: '60%',
    width: '100px',
    top: '25%',
    left: 0,
    pointerEvents: 'none',
    opacity: 0.5,
    '& focus-visible': {
      outline: 'none !important',
    },
  },
}))

export const useStylesProjects = makeStyles(() => ({
  boxWrapper: {
    display: 'grid',
    gridTemplateColumns: '1fr',
  },
  card: {
    borderRadius: '0 !important',
    padding: '1rem',
    boxShadow: 'none',
  },
}))

export const useStylesAwards = makeStyles(() => ({
  card: {
    borderRadius: '0 !important',
    padding: '1rem',
    boxShadow: 'none',
  },
  indigoDivider: {
    height: '1px',
    color: 'red',
    background: indigo[600],
    mt: 1,
    mb: 1,
    pt: 1,
    pb: 1,
  },
}))

export const useStylesBusinessOverview = makeStyles(() => ({
  row: {
    flex: '1 1 auto',
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
  },
  colLabel: {
    flex: '0 0 30%',
  },
  colValue: {
    flex: '0 0 70%',
  },
  boxWrapper: {
    display: 'grid',
    gridTemplateColumns: '4fr 1.5fr 1fr',
  },
  card: {
    borderRadius: '0px !important',
    boxShadow: 'none',
    padding: '1rem',
  },
}))

export const useStylesBusinessHeader = makeStyles(() => ({
  imgContainer: {
    display: 'flex',
    width: '150px',
    minHeight: '150px',
    height: '100%',
    objectFit: 'contain',
    justifyContent: 'center',
    alignContent: 'center',
    alignItems: 'center',
    justifyItems: 'center',

    padding: 1,
    boxShadow: 'rgba(0, 0, 0, 0.1) -4px 9px 25px -6px',
  },
  card: {
    borderRadius: '0px !important',
    boxShadow: 'none',
    overflow: 'visible',
  },
  img: {
    maxWidth: '100%',
    height: 'auto',
    padding: 'auto',
    margin: 'auto',
  },
}))

export const useStylesBusinessSearchResult = makeStyles(() => ({
  imgContainer: {
    display: 'flex',
    width: '1.5em',
    minHeight: '2em',
    height: '100%',
    objectFit: 'contain',
    justifyContent: 'center',
    alignContent: 'center',
    alignItems: 'center',
    justifyItems: 'center',
    background: 'tansparent',
    padding: 1,
    boxShadow: 'none',
  },
  card: {
    borderRadius: '0px !important',
    boxShadow: 'none',
    overflow: 'visible',
  },
  img: {
    maxWidth: '100%',
    height: 'auto',
    padding: 'auto',
    margin: 'auto',
  },
}))

export const useStylesBusinessImage = makeStyles(() => ({
  imgContainer: {
    display: 'flex',
    width: '1.5em',
    minHeight: '2em',
    height: '100%',
    objectFit: 'contain',
    justifyContent: 'center',
    alignContent: 'center',
    alignItems: 'center',
    justifyItems: 'center',
    background: 'tansparent',
    padding: 1,
    boxShadow: 'none',
  },
  card: {
    borderRadius: '0px !important',
    boxShadow: 'none',
    overflow: 'visible',
  },
  img: {
    maxWidth: '100%',
    height: 'auto',
    padding: 'auto',
    margin: 'auto',
  },
}))

export const useStylesSearchResult = makeStyles(() => ({
  imgContainer: {
    display: 'flex',
    width: '1.5em',
    minHeight: '2em',
    height: '100%',
    objectFit: 'contain',
    justifyContent: 'center',
    alignContent: 'center',
    alignItems: 'center',
    justifyItems: 'center',
    background: 'tansparent',
    padding: 1,
    boxShadow: 'none',
  },
  card: {
    borderRadius: '0px !important',
    boxShadow: 'none',
    overflow: 'visible',
  },
  img: {
    maxWidth: '100%',
    height: 'auto',
    padding: 'auto',
    margin: 'auto',
  },
}))

export const useStylesPersonsSearchResult = makeStyles(() => ({
  imgContainer: {
    display: 'flex',
    width: '1.5em',
    minHeight: '2em',
    height: '100%',
    objectFit: 'contain',
    justifyContent: 'center',
    alignContent: 'center',
    alignItems: 'center',
    justifyItems: 'center',
    background: 'tansparent',
    padding: 1,
    boxShadow: 'none',
  },
  card: {
    borderRadius: '0px !important',
    boxShadow: 'none',
    overflow: 'visible',
  },
  img: {
    maxWidth: '100%',
    height: 'auto',
    padding: 'auto',
    margin: 'auto',
  },
}))

export const useStylesToolbar = makeStyles(() => ({
  boxWrapper: {
    display: 'grid',
    gridTemplateColumns: '1fr auto',
  },
}))

export const useStylesConceptOverview = makeStyles(() => ({
  boxWrapper: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr 1fr',
  },
  card: {
    borderRadius: '0 !important',
    padding: '1rem',
  },
}))

export const BusinessItem = styled(Card)(({theme}) => ({
  ...theme.typography.body2,
  textAlign: 'center',
  color: theme.palette.text.secondary,
  height: 60,
  lineHeight: '60px',
}))

export const lightTheme = createTheme({palette: {mode: 'light'}})

export const useColors = makeStyles(() => ({
  lightBlue25WGrey: {
    backgroundColor: `${alpha(lightBlue[50], 0.5)} !important`,
    color: grey[900],
  },
  lightBlue15WGrey: {
    backgroundColor: `${alpha(lightBlue[50], 0.25)} !important`,
    color: grey[900],
  },
  lightAmber50WGrey: {
    backgroundColor: `${alpha(amber[100], 0.5)} !important`,
    color: grey[800],
  },
  lightAmber25WGrey: {
    backgroundColor: `${alpha(amber[50], 0.5)} !important`,
    color: grey[800],
  },
  lightAmber15WGrey: {
    backgroundColor: `${alpha(amber[50], 0.25)} !important`,
    color: grey[800],
  },
  lightTeal15WGrey: {
    backgroundColor: `${alpha(teal[50], 0.5)} !important`,
    color: grey[800],
  },
  lightPurple15WGrey: {
    backgroundColor: `${alpha(purple[100], 0.1)} !important`,
    color: grey[800],
  },
  pendingActions: {
    backgroundColor: `${alpha(orange[100], 0.2)} !important`,
    border: `3px solid ${alpha(red[400], 0.6)}`,
    color: grey[800],
  },
  lightRed15WGrey: {
    backgroundColor: `${alpha(red[100], 0.1)} !important`,
    color: grey[800],
  },
  darkblue: {
    backgroundColor: `${alpha(grey[400], 0.4)} !important`,
    color: blueGrey[900],
  },
  darkNavy: {
    backgroundColor: `${alpha('#083866', 0.6)} !important`,
    color: grey[800],
  },
}))

export const useTreeGraphStyles = makeStyles(() => ({
  node: {
    cursor: 'pointer',
    circle: {
      fill: 'rgb(247, 106, 214)',
      stroke: 'rgb(184, 37, 81)',
      strokeWidth: '1.5px',
    },
    text: {
      fontSize: '11px',
      fontFamily: 'Helvetica Neue, Helvetica, Arial, sans-serif',
      backgroundColor: 'black',
      fill: 'black',
    },
  },

  path: {
    '& link': {
      fill: 'none',
      stroke: '#2593b8',
      strokeWidth: '1.5px',
    },
  },
  breadcrumbHolder: {
    margin: '0 0 5px',
    padding: '0 0 5px',
    '& ul': {
      display: 'flex',
      listStyle: 'none',
    },
    '& li:last-of-type a': {
      cursor: 'text',
    },
    '& li a': {
      textDecoration: 'none',
      display: 'block',
    },
    '& li a:link': {
      textDecoration: 'none',
      display: 'block',
    },
    '& li a:visited': {
      textDecoration: 'none',
      display: 'block',
    },
  },
  counterBreadcrumbs: {
    '& li': {
      counterIncrement: 'mamun',
      margin: '0 8px 0 0',
    },
    '& li a': {
      position: 'relative',
      display: 'block',
      background: lightBlue[300],
      color: 'white',
      borderRadius: '0 3px 3px 0',
      padding: '5px 10px 4px 15px',
    },
    '& li a:link': {
      position: 'relative',
      display: 'block',
      background: lightBlue[300],
      color: 'white',
      borderRadius: '0 3px 3px 0',
      padding: '5px 10px 4px 15px',
    },
    '& li a:visited': {
      position: 'relative',
      display: 'block',
      background: lightBlue[300],
      color: 'white',
      borderRadius: '0 3px 3px 0',
      padding: '5px 10px 4px 15px',
    },
    '& li a:before': {
      content: 'counter(mamun)',
      display: 'inline-block',
      textAlign: 'center',
      padding: '2px 6px',
      background: teal[100],
      margin: '0 6px 0 -15px',
      borderRadius: '0 3px 3px 0',
      borderRight: '1px solid rgba(0, 0, 0, 0.4)',
    },
    '& li a:after': {
      content: '',
      position: 'absolute',
      border: '7px solid transparent',
      borderRight: 'none',
      borderLeft: `5px solid ${red[500]}`,
      right: '-14px',
      top: '50%',
      transform: 'translate(0, -50%)',
    },
    '& li a:hover': {
      background: lightBlue[200],
      cursor: 'pointer',
      boxShadow: 'none',
    },
  },
}))

export const useStylesBusinessCard = makeStyles(() => ({
  samCardStyle: {
    backgroundColor: `${alpha(grey[200], 0.3)} !important`,
  },
  samCardStyleGraphSelected: {
    backgroundColor: `${alpha(blue[200], 0.2)} !important`,
  },
  dsbsCardStyle: {
    backgroundColor: `${alpha(blueGrey[500], 0.1)} !important`,
    borderRadius: '0 !important',
  },
  infoCardStyle: {
    backgroundColor: `${alpha(grey[50], 1)} !important`,
  },
  infoSmallCardStyle: {
    backgroundColor: `${alpha(grey[100], 0.5)} !important`,
    borderRadius: '0 !important',
    border: '0 !important',
    marginBottom: 4,
    padding: 4,
    boxShadow: 'none',
  },
  relatedBusinessesCard: {
    borderRadius: '0 !important',
    border: '0 !important',
    marginBottom: 4,
    padding: 4,
    boxShadow: 'none',
  },
  relatedBusinesses: {
    backgroundColor: `${alpha(grey[100], 0.5)} !important`,
  },
  currency: {
    color: green[800],
  },
}))
